import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { RefreshCcw } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Input,
  Skeleton,
  cn,
} from '@helios/ui';
import { sessionScopeKey, useSessionScope, useSessionScopeFence } from '@/hooks/useSessionScope';
import {
  useExecutivePriorities,
  useExecutivePriority,
  useExecutivePriorityAction,
  useExecutivePriorityOptions,
} from '@/hooks/useExecutivePriorities';
import {
  executivePrioritiesClient as api,
  executivePriorityGrants as grants,
  type PriorityInput,
  type PriorityInvoiceScope,
  type PriorityList,
  type PriorityOptions,
  type PrioritySourceInput,
  type PriorityUpdate,
  type VisiblePriority,
} from '@/services/executivePrioritiesClient';
type Editor = { kind: 'create' } | { kind: 'edit' | 'archive' | 'complete'; row: VisiblePriority };
const DOCUMENT_ASSET_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const control =
  'h-8 w-full rounded-md border border-input bg-background px-2 text-xs text-foreground';
const isoLocal = (date: string) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
function isPriorityList(value: unknown): value is PriorityList {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<PriorityList>;
  const meta = candidate.meta;
  return (
    Array.isArray(candidate.items) &&
    candidate.permissions?.canRead === true &&
    typeof candidate.permissions.canCreate === 'boolean' &&
    typeof candidate.generatedAt === 'string' &&
    Boolean(meta) &&
    Number.isSafeInteger(meta?.page) &&
    (meta?.page ?? 0) > 0 &&
    Number.isSafeInteger(meta?.pageSize) &&
    (meta?.pageSize ?? 0) > 0 &&
    Number.isSafeInteger(meta?.total) &&
    (meta?.total ?? -1) >= 0 &&
    Number.isSafeInteger(meta?.totalPages) &&
    (meta?.totalPages ?? -1) >= 0
  );
}
export function ExecutivePriorityPanel({
  invoiceScope,
  heading = 'Executive priorities',
  compact = false,
  renderWorkspace,
  onRestoreFocus,
}: {
  invoiceScope?: PriorityInvoiceScope;
  heading?: string;
  compact?: boolean;
  /** Keep native editors mounted while a surrounding numeric snapshot refreshes. */
  renderWorkspace?: (panel: ReactNode) => ReactNode;
  /** Stable fallback when a surrounding refresh has removed the panel opener. */
  onRestoreFocus?: () => void;
} = {}) {
  const scope = useSessionScope(grants.read);
  if (!scope.enabled) {
    const denied = (
      <Card className="p-3">
        <p className="text-muted-foreground text-xs">
          Executive priorities require current task read access. Linked records retain their native
          grants.
        </p>
      </Card>
    );
    return renderWorkspace ? renderWorkspace(denied) : denied;
  }
  return (
    <PriorityContent
      key={JSON.stringify([
        sessionScopeKey(scope.identity),
        invoiceScope?.sourceType,
        invoiceScope?.sourceId,
      ])}
      invoiceScope={invoiceScope}
      heading={heading}
      compact={compact}
      renderWorkspace={renderWorkspace}
      onRestoreFocus={onRestoreFocus}
    />
  );
}
function PriorityContent({
  invoiceScope,
  heading,
  compact,
  renderWorkspace,
  onRestoreFocus,
}: {
  invoiceScope?: PriorityInvoiceScope;
  heading: string;
  compact: boolean;
  renderWorkspace?: (panel: ReactNode) => ReactNode;
  onRestoreFocus?: () => void;
}) {
  const { identity, isCurrent } = useSessionScopeFence(grants.read);
  const [pagination, setPagination] = useState({ page: 1, compact });
  // Page numbers refer to different windows in one-record and full-list modes.
  // Request the new mode's first page immediately, without remounting editors.
  const page = pagination.compact === compact ? pagination.page : 1;
  useEffect(() => {
    setPagination((current) => (current.compact === compact ? current : { page: 1, compact }));
  }, [compact]);
  const setPage = (update: (currentPage: number) => number) =>
    setPagination((current) => ({
      page: update(current.compact === compact ? current.page : 1),
      compact,
    }));
  const [editor, setEditor] = useState<Editor | null>(null);
  const [inspectedId, setInspectedId] = useState('');
  const lastInspectedId = useRef('');
  const skipInspectionRestore = useRef(false);
  const lastEditor = useRef<Editor | null>(null);
  const open = (value: Editor) => {
    lastEditor.current = value;
    setEditor(value);
  };
  const list = useExecutivePriorities(page, invoiceScope, compact ? 1 : undefined);
  const selectedId = editor && 'row' in editor ? editor.row.id : inspectedId;
  const detail = useExecutivePriority(selectedId);
  const workspace = useRef<HTMLElement>(null),
    newButton = useRef<HTMLButtonElement>(null),
    openers = useRef(new Map<string, HTMLButtonElement>());
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const current =
    !list.isFetching && !list.isError && isPriorityList(list.data) ? list.data : undefined;
  const currentDetail =
    detail.data && !detail.isFetching && !detail.isError && !detail.data.redacted
      ? detail.data
      : undefined;
  function restore() {
    if (!mounted.current || !isCurrent(identity)) return;
    const previous = lastEditor.current;
    const target =
      previous && 'row' in previous
        ? (openers.current.get(`${previous.kind}:${previous.row.id}`) ??
          openers.current.get(`edit:${previous.row.id}`) ??
          openers.current.get(`view:${previous.row.id}`))
        : newButton.current;
    if (target?.isConnected) target.focus({ preventScroll: true });
    else if (workspace.current?.isConnected) workspace.current.focus({ preventScroll: true });
    else onRestoreFocus?.();
  }
  const panel = (
    <section
      ref={workspace}
      role="region"
      aria-label={invoiceScope ? `${heading} workspace` : 'Executive priorities workspace'}
      tabIndex={-1}
      className={cn(
        'border-border bg-card flex h-full min-h-0 flex-col gap-2 overflow-hidden rounded-lg border p-3',
        compact &&
          'executive-priorities--compact !h-auto !rounded-none !border-0 !bg-transparent !p-0',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="hx-section-title text-sm font-semibold">{heading}</h2>
          <p className={cn('text-muted-foreground text-xs', compact && 'sr-only')}>
            Accountable human work · owner, due date, completion criteria and current evidence.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="xs"
            onClick={() => {
              void list.refetch();
              if (selectedId) void detail.refetch();
            }}
            disabled={list.isFetching}
            aria-label="Refresh priorities"
            title="Refresh priorities"
          >
            {compact ? (
              <RefreshCcw className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              'Refresh priorities'
            )}
          </Button>
          {current?.permissions.canCreate && (!invoiceScope || Boolean(invoiceScope.sourceId)) && (
            <Button ref={newButton} size="xs" onClick={() => open({ kind: 'create' })}>
              New priority
            </Button>
          )}
        </div>
      </div>
      {!invoiceScope?.sourceId && invoiceScope ? (
        <p className="text-muted-foreground text-xs">
          Open an exact invoice source record to assign a new follow-up. This list contains only the
          selected receivable or payable source cohort.
        </p>
      ) : null}
      <div className={compact ? 'min-h-0 flex-1 overflow-hidden' : 'min-h-0 flex-1 overflow-auto'}>
        {list.isLoading || list.isFetching ? (
          <div role="status" aria-label="Loading executive priorities">
            <Skeleton className={compact ? 'h-12 w-full' : 'h-44 w-full'} />
          </div>
        ) : list.isError || !current ? (
          compact ? (
            <div role="alert" className="border-border space-y-2 rounded-lg border p-3 text-xs">
              <p className="font-semibold">Executive priorities unavailable</p>
              <p className="text-muted-foreground">Refresh the current authorized task source.</p>
              <Button size="xs" onClick={() => void list.refetch()}>
                Retry priorities
              </Button>
            </div>
          ) : (
            <ErrorState
              title="Executive priorities unavailable"
              description={
                list.error instanceof Error
                  ? list.error.message
                  : 'Refresh the current authorized task source.'
              }
              action={
                <Button size="xs" onClick={() => void list.refetch()}>
                  Retry priorities
                </Button>
              }
            />
          )
        ) : !current.items.length ? (
          compact ? (
            <div className="text-muted-foreground text-xs" role="status">
              <p className="text-foreground font-medium">No recorded executive priorities</p>
              <p>
                Create an accountable task. This register does not establish all outstanding work.
              </p>
            </div>
          ) : (
            <EmptyState
              title="No recorded executive priorities"
              description="Create an accountable task. An empty local register does not prove that the enterprise has no outstanding work."
            />
          )
        ) : (
          <div className="space-y-2">
            {current.items.map((row) =>
              row.redacted ? (
                <div
                  key={row.id}
                  className="border-border text-muted-foreground rounded-md border p-3 text-xs"
                >
                  Restricted linked priority · source or evidence access is {row.sourceState}.
                  Copied task details are withheld.
                </div>
              ) : compact ? (
                <article
                  key={row.id}
                  className="border-border bg-background/50 space-y-3 rounded-lg border p-3 text-xs"
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="line-clamp-2 min-w-0 break-words font-semibold">{row.title}</h3>
                    <Badge
                      className="shrink-0"
                      variant={row.status === 'COMPLETED' ? 'success' : 'neutral'}
                    >
                      {row.status.replaceAll('_', ' ')}
                    </Badge>
                  </div>
                  <dl className="grid grid-cols-2 gap-2">
                    <div className="min-w-0">
                      <dt className="text-muted-foreground">Accountable owner</dt>
                      <dd className="truncate font-medium" title={row.owner.name}>
                        {row.owner.name}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Due</dt>
                      <dd className="tabular-nums">
                        {new Date(row.dueAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </dd>
                    </div>
                  </dl>
                  <p className="text-muted-foreground">
                    {row.evidence.length} pinned document{row.evidence.length === 1 ? '' : 's'} ·{' '}
                    {row.sourceState} source{!row.owner.active ? ' · owner inactive' : ''}
                  </p>
                  <Button
                    ref={(node) => {
                      if (node) openers.current.set(`view:${row.id}`, node);
                      else openers.current.delete(`view:${row.id}`);
                    }}
                    className="w-full"
                    size="xs"
                    variant="outline"
                    aria-label={`View task details ${row.title}`}
                    onClick={() => {
                      lastInspectedId.current = row.id;
                      setInspectedId(row.id);
                    }}
                  >
                    View task details
                  </Button>
                </article>
              ) : (
                <article key={row.id} className="border-border rounded-md border p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="break-words text-xs font-semibold">{row.title}</h3>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Owner: {row.owner.name}
                        {!row.owner.active ? ' · inactive; reassign before completion' : ''} · Due:{' '}
                        {new Date(row.dueAt).toLocaleString()}
                      </p>
                    </div>
                    <Badge variant={row.status === 'COMPLETED' ? 'success' : 'neutral'}>
                      {row.status.replaceAll('_', ' ')}
                    </Badge>
                  </div>
                  {row.description && <p className="mt-2 text-xs">{row.description}</p>}
                  <p className="mt-2 text-xs">
                    <span className="text-muted-foreground">Complete when: </span>
                    {row.completionCriteria}
                  </p>
                  <div className="text-muted-foreground mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
                    <span>{row.severity} priority</span>
                    {row.source ? (
                      <Link className="underline underline-offset-2" to={row.source.href}>
                        Source: {row.source.title} · {row.sourceState}
                      </Link>
                    ) : (
                      <span>Standalone human task</span>
                    )}
                    <span>
                      {row.evidence.length} pinned evidence document
                      {row.evidence.length === 1 ? '' : 's'} ·{' '}
                      {row.evidence.some((pin) => pin.state === 'stale')
                        ? 'changed evidence'
                        : row.evidence.length
                          ? 'current'
                          : 'required before completion'}
                    </span>
                  </div>
                  {row.evidence.length > 0 && (
                    <ul className="text-muted-foreground mt-2 space-y-1 text-xs">
                      {row.evidence.map((pin) => (
                        <li key={pin.id}>
                          <Link className="underline underline-offset-2" to={pin.href}>
                            {pin.title} · pinned {pin.documentVersion}
                          </Link>{' '}
                          · {pin.state}
                        </li>
                      ))}
                    </ul>
                  )}
                  {row.completionNote && (
                    <p className="text-muted-foreground mt-2 text-xs">
                      Completion: {row.completionNote} ·{' '}
                      {row.completedAt
                        ? new Date(row.completedAt).toLocaleString()
                        : 'Time unavailable'}
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {row.permissions.canUpdate && (
                      <Button
                        ref={(node) => {
                          if (node) openers.current.set(`edit:${row.id}`, node);
                          else openers.current.delete(`edit:${row.id}`);
                        }}
                        variant="outline"
                        size="xs"
                        aria-label={`Edit priority ${row.title}`}
                        onClick={() => open({ kind: 'edit', row })}
                      >
                        Edit / repin
                      </Button>
                    )}
                    {row.permissions.canComplete && (
                      <Button
                        ref={(node) => {
                          if (node) openers.current.set(`complete:${row.id}`, node);
                          else openers.current.delete(`complete:${row.id}`);
                        }}
                        variant="outline"
                        size="xs"
                        onClick={() => open({ kind: 'complete', row })}
                      >
                        Complete human task
                      </Button>
                    )}
                    {row.permissions.canArchive && (
                      <Button
                        ref={(node) => {
                          if (node) openers.current.set(`archive:${row.id}`, node);
                          else openers.current.delete(`archive:${row.id}`);
                        }}
                        variant="ghost"
                        size="xs"
                        onClick={() => open({ kind: 'archive', row })}
                      >
                        Archive
                      </Button>
                    )}
                  </div>
                  {row.status !== 'COMPLETED' &&
                    row.status !== 'ARCHIVED' &&
                    !row.permissions.canComplete && (
                      <p className="text-muted-foreground mt-2 text-xs">
                        Completion requires an active owner, current linked source, at least one
                        current evidence document, and completion access. Edit to explicitly repin
                        changed sources.
                      </p>
                    )}
                </article>
              ),
            )}
          </div>
        )}
      </div>
      {current && (
        <div className="border-border text-muted-foreground flex shrink-0 flex-wrap items-center justify-between gap-2 border-t pt-2 text-xs">
          <span
            title={`Observed ${new Date(current.generatedAt).toLocaleString()}; restricted records are withheld.`}
          >
            {compact ? (
              `${current.meta.total} task records · page ${current.meta.page} of ${Math.max(1, current.meta.totalPages)}`
            ) : (
              <>
                Observed {new Date(current.generatedAt).toLocaleString()} · page {current.meta.page}{' '}
                of {Math.max(1, current.meta.totalPages)} · {current.meta.total} task records;
                restricted records are withheld.
              </>
            )}
          </span>
          {!compact || current.meta.totalPages > 1 ? (
            <div className="flex gap-1">
              <Button
                size="xs"
                variant="ghost"
                aria-label="Previous priorities"
                disabled={page <= 1}
                onClick={() => setPage((value) => value - 1)}
              >
                {compact ? 'Previous' : 'Previous priorities'}
              </Button>
              <Button
                size="xs"
                variant="ghost"
                aria-label="Next priorities"
                disabled={page >= current.meta.totalPages}
                onClick={() => setPage((value) => value + 1)}
              >
                {compact ? 'Next' : 'Next priorities'}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
  return (
    <>
      {renderWorkspace ? renderWorkspace(panel) : panel}
      <Dialog
        open={Boolean(inspectedId)}
        onOpenChange={(isOpen) => {
          if (!isOpen) setInspectedId('');
        }}
      >
        <DialogContent
          className="max-w-2xl"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (skipInspectionRestore.current) {
              skipInspectionRestore.current = false;
              return;
            }
            if (!mounted.current || !isCurrent(identity)) return;
            const target = openers.current.get(`view:${lastInspectedId.current}`);
            if (target?.isConnected) target.focus({ preventScroll: true });
            else if (workspace.current?.isConnected)
              workspace.current.focus({ preventScroll: true });
            else onRestoreFocus?.();
          }}
        >
          <DialogHeader>
            <DialogTitle>Human task details</DialogTitle>
            <DialogDescription>
              Recorded internal work and pinned evidence. Task completion does not confirm
              collection, payment or bank settlement.
            </DialogDescription>
          </DialogHeader>
          {detail.isLoading || detail.isFetching ? (
            <Skeleton className="h-48 w-full" aria-label="Loading task details" />
          ) : detail.isError || !detail.data ? (
            <div role="alert" className="space-y-2 text-sm">
              <p>Task details are unavailable with current access.</p>
              <Button size="xs" variant="outline" onClick={() => void detail.refetch()}>
                Retry task details
              </Button>
            </div>
          ) : detail.data.redacted ? (
            <p className="text-muted-foreground text-sm">
              Restricted linked priority · source or evidence access is {detail.data.sourceState}.
              Copied task details are withheld.
            </p>
          ) : currentDetail ? (
            <PriorityTaskDetails
              row={currentDetail}
              onAction={(kind) => {
                skipInspectionRestore.current = true;
                setInspectedId('');
                open({ kind, row: currentDetail });
              }}
            />
          ) : null}
          <Button size="xs" variant="outline" className="mt-4" onClick={() => setInspectedId('')}>
            Close task details
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog
        open={editor !== null}
        onOpenChange={(open) => {
          if (!open) setEditor(null);
        }}
      >
        <DialogContent
          className="max-w-2xl"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            restore();
          }}
        >
          {editor && (
            <PriorityEditor
              key={editor.kind === 'create' ? 'create' : `${editor.kind}:${editor.row.id}`}
              editor={editor}
              current={currentDetail}
              invoiceScope={invoiceScope}
              close={() => setEditor(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Full native task evidence/actions remain available from the bounded invoice summary. */
function PriorityTaskDetails({
  row,
  onAction,
}: {
  row: VisiblePriority;
  onAction: (kind: 'edit' | 'archive' | 'complete') => void;
}) {
  return (
    <article className="border-border space-y-3 rounded-lg border p-4 text-sm">
      <div className="flex items-start justify-between gap-3">
        <h3 className="min-w-0 break-words font-semibold">{row.title}</h3>
        <Badge className="shrink-0" variant={row.status === 'COMPLETED' ? 'success' : 'neutral'}>
          {row.status.replaceAll('_', ' ')}
        </Badge>
      </div>
      <p className="text-muted-foreground text-xs">
        Owner: {row.owner.name}
        {!row.owner.active ? ' · inactive; reassign before completion' : ''} · Due:{' '}
        {new Date(row.dueAt).toLocaleString()}
      </p>
      {row.description ? <p className="break-words">{row.description}</p> : null}
      <p className="break-words">
        <span className="text-muted-foreground">Complete when: </span>
        {row.completionCriteria}
      </p>
      <div className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 text-xs">
        <span>{row.severity} priority</span>
        {row.source ? (
          <Link className="underline underline-offset-2" to={row.source.href}>
            Source: {row.source.title} · {row.sourceState}
          </Link>
        ) : (
          <span>Standalone human task</span>
        )}
        <span>
          {row.evidence.length} pinned evidence document{row.evidence.length === 1 ? '' : 's'} ·{' '}
          {row.evidence.some((pin) => pin.state === 'stale')
            ? 'changed evidence'
            : row.evidence.length
              ? 'current'
              : 'required before completion'}
        </span>
      </div>
      {row.evidence.length ? (
        <ul className="text-muted-foreground space-y-1 text-xs">
          {row.evidence.map((pin) => (
            <li key={pin.id}>
              <Link className="underline underline-offset-2" to={pin.href}>
                {pin.title} · pinned {pin.documentVersion}
              </Link>{' '}
              · {pin.state}
            </li>
          ))}
        </ul>
      ) : null}
      {row.completionNote ? (
        <p className="text-muted-foreground text-xs">
          Completion: {row.completionNote} ·{' '}
          {row.completedAt ? new Date(row.completedAt).toLocaleString() : 'Time unavailable'}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {row.permissions.canUpdate ? (
          <Button
            size="xs"
            variant="outline"
            aria-label={`Edit priority ${row.title}`}
            onClick={() => onAction('edit')}
          >
            Edit / repin
          </Button>
        ) : null}
        {row.permissions.canComplete ? (
          <Button size="xs" variant="outline" onClick={() => onAction('complete')}>
            Complete human task
          </Button>
        ) : null}
        {row.permissions.canArchive ? (
          <Button size="xs" variant="ghost" onClick={() => onAction('archive')}>
            Archive
          </Button>
        ) : null}
      </div>
      {row.status !== 'COMPLETED' && row.status !== 'ARCHIVED' && !row.permissions.canComplete ? (
        <p className="text-muted-foreground text-xs">
          Completion requires an active owner, current linked source, at least one current evidence
          document, and completion access. Edit to explicitly repin changed sources.
        </p>
      ) : null}
    </article>
  );
}
function PriorityEditor({
  editor,
  current,
  invoiceScope,
  close,
}: {
  editor: Editor;
  current?: VisiblePriority;
  invoiceScope?: PriorityInvoiceScope;
  close: () => void;
}) {
  const [docSearch, setDocSearch] = useState('');
  const [docId, setDocId] = useState('');
  const documentAssetId = DOCUMENT_ASSET_ID.test(docId.trim()) ? docId.trim() : undefined;
  const optionsQuery = useExecutivePriorityOptions(
    editor.kind === 'create' || editor.kind === 'edit',
    {
      q: docSearch.trim() || undefined,
      documentAssetId,
      ...invoiceScope,
    },
  );
  const options =
    optionsQuery.data && !optionsQuery.isFetching && !optionsQuery.isError
      ? optionsQuery.data
      : undefined;
  const fresh =
    editor.kind === 'create'
      ? Boolean(options)
      : Boolean(current && current.id === editor.row.id && current.version === editor.row.version);
  const row = editor.kind === 'create' ? undefined : (current ?? editor.row);
  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {editor.kind === 'create'
            ? 'Create executive priority'
            : editor.kind === 'edit'
              ? 'Edit executive priority'
              : editor.kind === 'complete'
                ? 'Complete human priority'
                : 'Archive executive priority'}
        </DialogTitle>
        <DialogDescription>
          Human work only. This records no source approval, provider execution, payment or delivery.
        </DialogDescription>
      </DialogHeader>
      {!fresh && (
        <p role="status" className="text-muted-foreground mb-3 text-xs">
          Current authorized sources are loading or unavailable. Your draft remains; saving waits
          for current access and evidence.
        </p>
      )}
      {optionsQuery.isError && (
        <Button size="xs" variant="outline" onClick={() => void optionsQuery.refetch()}>
          Retry task options
        </Button>
      )}
      {editor.kind === 'create' || editor.kind === 'edit' ? (
        <PriorityForm
          initial={editor.kind === 'edit' ? editor.row : undefined}
          current={row}
          options={options}
          docSearch={docSearch}
          docId={docId}
          onDocSearch={setDocSearch}
          onDocId={setDocId}
          fresh={fresh}
          invoiceScope={invoiceScope}
          close={close}
        />
      ) : (
        row && <PriorityDecision kind={editor.kind} row={row} fresh={fresh} close={close} />
      )}
    </>
  );
}
function useFinish(close: () => void) {
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return (current: boolean) => {
    if (mounted.current && current) close();
  };
}
function PriorityForm({
  initial,
  current,
  options,
  docSearch,
  docId,
  onDocSearch,
  onDocId,
  fresh,
  invoiceScope,
  close,
}: {
  initial?: VisiblePriority;
  invoiceScope?: PriorityInvoiceScope;
  current?: VisiblePriority;
  options?: PriorityOptions;
  docSearch: string;
  docId: string;
  onDocSearch: (value: string) => void;
  onDocId: (value: string) => void;
  fresh: boolean;
  close: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? ''),
    [description, setDescription] = useState(initial?.description ?? ''),
    [criteria, setCriteria] = useState(initial?.completionCriteria ?? ''),
    [owner, setOwner] = useState(initial?.owner.id ?? ''),
    [due, setDue] = useState(initial ? isoLocal(initial.dueAt) : '');
  const [severity, setSeverity] = useState<PriorityInput['severity']>(
    (initial?.severity as PriorityInput['severity']) ?? 'MODERATE',
  );
  const [status, setStatus] = useState<'OPEN' | 'IN_PROGRESS'>(
    initial?.status === 'IN_PROGRESS' ? 'IN_PROGRESS' : 'OPEN',
  );
  const [sourceKey, setSourceKey] = useState(
      initial?.source
        ? `${initial.source.type}:${initial.source.id}`
        : invoiceScope?.sourceId
          ? `${invoiceScope.sourceType}:${invoiceScope.sourceId}`
          : '',
    ),
    [repinSource, setRepinSource] = useState(false);
  const [retainedSource, setRetainedSource] = useState<PriorityOptions['sources'][number] | null>(
    null,
  );
  const [evidence, setEvidence] = useState(
      initial?.evidence.map((pin) => pin.documentAssetId) ?? [],
    ),
    [repinEvidence, setRepinEvidence] = useState(false);
  const mutation = useExecutivePriorityAction(
    initial ? grants.update : grants.create,
    (input: PriorityInput | PriorityUpdate, options) =>
      initial
        ? api.update(initial.id, input as PriorityUpdate, options)
        : api.create(input as PriorityInput, options),
  );
  const finish = useFinish(close);
  const selectedSource =
    retainedSource && `${retainedSource.type}:${retainedSource.id}` === sourceKey
      ? retainedSource
      : options?.sources.find((item) => `${item.type}:${item.id}` === sourceKey);
  const source: PrioritySourceInput | null | undefined = !sourceKey
    ? null
    : invoiceScope &&
        initial &&
        !repinSource &&
        current?.source &&
        `${current.source.type}:${current.source.id}` === sourceKey
      ? {
          type: current.source.type,
          id: current.source.id,
          expectedVersion: current.source.pinnedVersion,
        }
      : selectedSource
        ? {
            type: selectedSource.type,
            id: selectedSource.id,
            expectedVersion: selectedSource.version,
          }
        : current?.source && `${current.source.type}:${current.source.id}` === sourceKey
          ? {
              type: current.source.type,
              id: current.source.id,
              expectedVersion: current.source.currentVersion,
            }
          : undefined;
  const currentInvoiceChoice = invoiceScope
    ? options?.sources.find(
        (candidate) =>
          candidate.type === invoiceScope.sourceType && candidate.id === invoiceScope.sourceId,
      )
    : undefined;
  useEffect(() => {
    if (currentInvoiceChoice && !retainedSource) setRetainedSource(currentInvoiceChoice);
  }, [currentInvoiceChoice, retainedSource]);
  const invoicePinCurrent =
    !invoiceScope ||
    Boolean(
      source &&
        source.type === invoiceScope.sourceType &&
        source.id === invoiceScope.sourceId &&
        currentInvoiceChoice &&
        source.expectedVersion === currentInvoiceChoice.version,
    );
  const sourceResolved =
    (Boolean(initial && !repinSource) || source !== undefined) &&
    (!invoiceScope || Boolean(initial && !repinSource) || invoicePinCurrent);
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!fresh || !sourceResolved) return;
    const values: PriorityInput = {
      title,
      description: description || null,
      ownerId: owner,
      dueAt: new Date(due).toISOString(),
      severity,
      completionCriteria: criteria,
      ...(!initial || repinSource ? { source } : {}),
      ...(!initial || repinEvidence ? { evidenceDocumentAssetIds: evidence } : {}),
    };
    try {
      await mutation.mutateAsync(
        initial ? { ...values, status, expectedVersion: initial.version } : values,
      );
      finish(mutation.isCurrent());
    } catch {
      /* scoped action error below */
    }
  }
  const sources = [...(options?.sources ?? [])];
  if (
    retainedSource &&
    !sources.some((item) => item.type === retainedSource.type && item.id === retainedSource.id)
  )
    sources.push(retainedSource);
  const docs = [...(options?.evidence ?? [])];
  for (const pin of initial?.evidence ?? [])
    if (!docs.some((item) => item.id === pin.documentAssetId))
      docs.push({ id: pin.documentAssetId, title: pin.title, href: pin.href });
  return (
    <form className="space-y-3" onSubmit={(event) => void save(event)}>
      <Field label="Priority title">
        <Input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          required
          maxLength={200}
        />
      </Field>
      <Field label="Work and context">
        <textarea
          className={`${control} min-h-16`}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          maxLength={8000}
        />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Accountable owner">
          <select
            className={control}
            value={owner}
            onChange={(event) => setOwner(event.target.value)}
            required
          >
            <option value="">Choose active staff owner</option>
            {initial && !options?.owners.some((item) => item.id === initial.owner.id) && (
              <option value={initial.owner.id}>
                Previously assigned · choose current active owner
              </option>
            )}
            {options?.owners.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
                {item.title ? ` · ${item.title}` : ''}
              </option>
            ))}
          </select>
        </Field>
        <Field label={`Due date/time · ${Intl.DateTimeFormat().resolvedOptions().timeZone}`}>
          <Input
            type="datetime-local"
            value={due}
            onChange={(event) => setDue(event.target.value)}
            required
          />
        </Field>
        <Field label="Human priority severity">
          <select
            className={control}
            value={severity}
            onChange={(event) => setSeverity(event.target.value as typeof severity)}
          >
            {['LOW', 'MODERATE', 'HIGH', 'CRITICAL'].map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </Field>
        {initial && (
          <Field label="Task work status">
            <select
              className={control}
              value={status}
              onChange={(event) => setStatus(event.target.value as typeof status)}
            >
              <option value="OPEN">Open</option>
              <option value="IN_PROGRESS">In progress</option>
            </select>
          </Field>
        )}
      </div>
      <Field label="Complete when · observable criteria">
        <textarea
          className={`${control} min-h-20`}
          value={criteria}
          onChange={(event) => setCriteria(event.target.value)}
          required
          maxLength={8000}
        />
      </Field>
      <Field
        label={
          invoiceScope ? 'Exact invoice source · required' : 'Linked authorized source · optional'
        }
      >
        <select
          className={control}
          value={sourceKey}
          disabled={Boolean(invoiceScope)}
          onChange={(event) => {
            const key = event.target.value;
            setSourceKey(key);
            setRetainedSource(sources.find((item) => `${item.type}:${item.id}` === key) ?? null);
            setRepinSource(true);
          }}
        >
          <option value="">Standalone human task</option>
          {initial?.source &&
            !sources.some(
              (item) => item.id === initial.source?.id && item.type === initial.source.type,
            ) && (
              <option value={`${initial.source.type}:${initial.source.id}`}>
                {initial.source.title} · current linked source
              </option>
            )}
          {sources.map((item) => (
            <option key={`${item.type}:${item.id}`} value={`${item.type}:${item.id}`}>
              {item.type} · {item.title}
            </option>
          ))}
        </select>
      </Field>
      {invoiceScope ? (
        <div className="break-all text-xs">
          <p>Selected invoice fingerprint: {source?.expectedVersion ?? 'Unavailable'}</p>
          {currentInvoiceChoice && !invoicePinCurrent ? (
            <>
              <p role="status">
                The invoice source changed. Review its current source before explicitly re-pinning;
                your owner and due-date draft remains.
              </p>
              <Button
                type="button"
                size="xs"
                variant="outline"
                onClick={() => {
                  setRetainedSource(currentInvoiceChoice);
                  setRepinSource(true);
                }}
              >
                Review current invoice source pin
              </Button>
            </>
          ) : null}
        </div>
      ) : null}
      {!sourceResolved && (
        <p role="status" className="text-muted-foreground text-xs">
          The selected linked source is unavailable. Choose an authorized source or clear the link
          before saving.
        </p>
      )}
      {initial && (
        <label className="flex gap-2 text-xs">
          <input
            type="checkbox"
            checked={repinSource}
            onChange={(event) => setRepinSource(event.target.checked)}
          />
          Explicitly re-pin the selected source version after reviewing it. Title/owner edits
          preserve the existing source pin.
        </label>
      )}
      <fieldset className="border-border space-y-2 rounded-md border p-3">
        <legend className="px-1 text-xs">
          Pinned current evidence · required before human completion
        </legend>
        <div className="grid gap-2 sm:grid-cols-2">
          <Field label="Search evidence titles">
            <Input
              value={docSearch}
              onChange={(event) => onDocSearch(event.target.value)}
              maxLength={200}
              placeholder="Title contains"
            />
          </Field>
          <Field label="Known document UUID">
            <Input
              value={docId}
              onChange={(event) => onDocId(event.target.value)}
              maxLength={36}
              placeholder="Resolve a specific document"
            />
          </Field>
        </div>
        {docs.length ? (
          docs.map((item) => (
            <label key={item.id} className="flex gap-2 text-xs">
              <input
                type="checkbox"
                checked={evidence.includes(item.id)}
                onChange={(event) => {
                  setRepinEvidence(true);
                  setEvidence((items) =>
                    event.target.checked
                      ? [...items, item.id]
                      : items.filter((id) => id !== item.id),
                  );
                }}
              />
              {item.title}
            </label>
          ))
        ) : (
          <p className="text-muted-foreground text-xs">
            No authorized uploaded evidence choices were observed. Refresh options or complete the
            upload in Documents.
          </p>
        )}
        {initial && (
          <label className="flex gap-2 text-xs">
            <input
              type="checkbox"
              checked={repinEvidence}
              onChange={(event) => setRepinEvidence(event.target.checked)}
            />
            Explicitly replace evidence pins with the selected documents’ current immutable
            versions.
          </label>
        )}
      </fieldset>
      {options && <p className="text-muted-foreground text-xs">{options.coverage.reason}</p>}
      <End
        error={mutation.error}
        pending={mutation.isPending}
        disabled={!fresh || !sourceResolved || Boolean(initial && !current?.permissions.canUpdate)}
        close={close}
        label={initial ? 'Save priority changes' : 'Create human priority'}
      />
    </form>
  );
}
function PriorityDecision({
  kind,
  row,
  fresh,
  close,
}: {
  kind: 'archive' | 'complete';
  row: VisiblePriority;
  fresh: boolean;
  close: () => void;
}) {
  const [note, setNote] = useState('');
  const mutation = useExecutivePriorityAction(
    kind === 'archive' ? grants.archive : grants.complete,
    (_input: void, options) =>
      kind === 'archive'
        ? api.archive(row.id, row.version, options)
        : api.complete(row.id, row.version, note, options),
  );
  const finish = useFinish(close);
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!fresh) return;
    try {
      await mutation.mutateAsync();
      finish(mutation.isCurrent());
    } catch {
      /* scoped error below */
    }
  }
  return (
    <form className="space-y-3" onSubmit={(event) => void save(event)}>
      <p className="text-sm font-medium">{row.title}</p>
      <p className="text-muted-foreground text-xs">
        Owner: {row.owner.name} · exact task version {row.version}
      </p>
      <p className="text-xs">Complete when: {row.completionCriteria}</p>
      {kind === 'complete' ? (
        <>
          <p className="text-muted-foreground text-xs">
            Source: {row.sourceState} · {row.evidence.length} pinned current evidence documents.
            This marks only this human task complete.
          </p>
          <Field label="Completion note and evidence reviewed">
            <textarea
              className={`${control} min-h-24`}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              required
              maxLength={8000}
            />
          </Field>
        </>
      ) : (
        <p className="text-muted-foreground text-xs">
          Archive retains the task, its audit history, and any recorded human completion.
        </p>
      )}
      <End
        error={mutation.error}
        pending={mutation.isPending}
        disabled={
          !fresh ||
          (kind === 'complete' ? !row.permissions.canComplete : !row.permissions.canArchive)
        }
        close={close}
        label={kind === 'complete' ? 'Record human completion' : 'Archive priority'}
      />
    </form>
  );
}
function End({
  error,
  pending,
  disabled,
  close,
  label,
}: {
  error: unknown;
  pending: boolean;
  disabled: boolean;
  close: () => void;
  label: string;
}) {
  return (
    <>
      {Boolean(error) && (
        <p role="alert" className="text-critical text-xs">
          {error instanceof Error
            ? error.message
            : 'The scoped save failed. Refresh before retrying.'}
        </p>
      )}
      <div className="border-border flex justify-end gap-2 border-t pt-3">
        <Button type="button" size="sm" variant="ghost" onClick={close} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={disabled || pending}>
          {pending ? 'Recording…' : label}
        </Button>
      </div>
    </>
  );
}
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid min-w-0 gap-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
